import {
    defineComponent,
    defineAsyncComponent,
    ref,
    reactive,
    toRef,
    watch,
    computed
} from 'vue'
import { BlockModule } from '../common'
import type { ProductComparisonProductCategory, CoverageItem, Coverages, ProductComparisonItem, SelectedItems } from '@/types'
import { useProductComparisonAsync, useProductComparisonPriceAsync, useProductComparisonProductsAsync } from '@/datasources'
import { useNavContext } from '../main-navigation/composables'
import { useIntersectionObserver, useBreakpoints, useUrlSearchParams } from '@vueuse/core'
import { tailwindBreakpoints } from '../tailwind'
import { scrollToTopOfPage } from '@/lib/scrolling'

new BlockModule({
    selector: '.block-product-comparison',
    vueComponent: defineComponent({
        name: 'block-product-comparison',
        components: {
            Accordion: defineAsyncComponent(
                () => import('@/components/ui/accordion/Accordion.vue'),
            ),
            AccordionContent: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.AccordionContent),
            ),
            AccordionItem: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.AccordionItem),
            ),
            AccordionTrigger: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.AccordionTrigger),
            ),
            LazyImage: defineAsyncComponent(
                () => import('@/components/website/lazy-image/lazy-image.vue'),
            ),
            SelectRoot: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.SelectRoot)
            ),
            SelectContent: defineAsyncComponent(
                () => import('@/components/ui/select/SelectContent.vue'),
            ),
            SelectItem: defineAsyncComponent(
                () => import('@/components/ui/select/SelectItem.vue')
            ),
            SelectItemIndicator: defineAsyncComponent(() =>
                import('radix-vue').then((x) => x.SelectItemIndicator),
            ),
            SelectItemText: defineAsyncComponent(() =>
                import('radix-vue').then((x) => x.SelectItemText),
            ),
            SelectValue: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.SelectValue)
            ),
            SelectTrigger: defineAsyncComponent(
                () => import('@/components/ui/select/SelectTrigger.vue'),
            ),
            MsigButton: defineAsyncComponent(
                () => import('@/components/website/button/button.vue'),
            ),
            MsigBadge: defineAsyncComponent(
                () => import('@/components/ui/badge/Badge.vue'),
            ),
            FadeInOut: defineAsyncComponent(
                () => import('@/components/website/transition/FadeInOut.vue')
            )
        },
        props: {
            firstProductCategory: String,
            productCategories: String
        },
        setup(props) {
            const isBusyBase = ref(false)
            const isBusyProducts = ref(false)
            const isBusyPrice = ref(false)
            const target = ref(null)
            const targetIsVisible = ref(false)
            const breakpoints = useBreakpoints(tailwindBreakpoints)
            const mdAndGreater = breakpoints.greaterOrEqual('md')
            const { isNavHidden } = useNavContext()

            const urlParams = useUrlSearchParams('history')
            const categoryParam = urlParams.category as string


            useIntersectionObserver(
                target, ([entry]) => {
                targetIsVisible.value = entry.isIntersecting
            }, {threshold: 1.0 }) //adjust threshold according to the sensitivity of target detection for sticky navbar

            const selectedItems = reactive<SelectedItems>({
                first: null,
                second: null,
                third: null,
            })

            const filters = reactive({
                categoryKey: '',
                productKeys: [] as number[]
            })

            const productComparisonAsync = useProductComparisonAsync(
                toRef(filters, 'categoryKey'),
                { evaluating: isBusyBase },
            )

            const productComparisonProductsAsync = useProductComparisonProductsAsync(
                toRef(filters, 'categoryKey'),
                toRef(filters, 'productKeys'),
                { evaluating: isBusyProducts },
            )
            
            const pageOffsetTop = computed(() => window.getComputedStyle(document.documentElement).getPropertyValue('--page-offset-top'))

            const coverageGroups = computed(() => {
                const uniqueGroups: Record<
                    string,
                    {
                        coverageItems: CoverageItem[]
                        coverageGroupKey: string
                        coverageGroup: string
                    }
                > = {}

                productComparisonProductsAsync.value.forEach((product: ProductComparisonItem) => {
                    product.coverages?.forEach((group: Coverages) => {
                        if (!uniqueGroups[group.coverageGroupKey]) {
                            uniqueGroups[group.coverageGroupKey] = {
                                coverageGroupKey: group.coverageGroupKey,
                                coverageGroup: group.coverageGroup,
                                coverageItems: group.coverageItems,
                            }
                        }
                    })
                })

                return Object.values(uniqueGroups)
            })

            const isItemCovered = (
                product: { coverages: Coverages[] },
                coverageGroupKey: string,
                itemKey: string,
            ): boolean => {
                if (!product || !product.coverages) return false

                const coverageGroup = product.coverages.find(
                    (coverage) => coverage.coverageGroupKey === coverageGroupKey,
                )
                if (!coverageGroup) return false

                const coverageItem = coverageGroup.coverageItems.find(
                    (coverageItem) => coverageItem.key === itemKey,
                )

                return !!coverageItem?.isCoveredBy
            }

            watch(
                () => [selectedItems.first, selectedItems.second, selectedItems.third],
                ([newFirst, newSecond, newThird], [oldFirst, oldSecond, oldThird]) => {
                    const newItems = { first: newFirst, second: newSecond, third: newThird }

                    // 1) Handle the case: first == second == third
                    if (
                        newItems.first &&
                        newItems.second &&
                        newItems.third &&
                        newItems.first === newItems.second &&
                        newItems.second === newItems.third
                    ) {
                        // For example, revert the third slot to its old occupant
                        // (So the user effectively changed only two slots).
                        newItems.third = oldThird
                    }

                    // 2) Handle first == second
                    if (newItems.first && newItems.second && newItems.first === newItems.second) {
                        // Symmetrical swap: slot1 becomes oldSecond, slot2 becomes oldFirst
                        newItems.first = oldSecond
                        newItems.second = oldFirst
                    }

                    // 3) Handle first == third
                    if (newItems.first && newItems.third && newItems.first === newItems.third) {
                        const temp = newItems.first
                        newItems.first = oldThird
                        newItems.third = oldFirst
                    }

                    // 4) Handle second == third
                    if (newItems.second && newItems.third && newItems.second === newItems.third) {
                        newItems.second = oldThird
                        newItems.third = oldSecond
                    }

                    // 5) Assign final
                    selectedItems.first = newItems.first
                    selectedItems.second = newItems.second
                    selectedItems.third = newItems.third
                },
            )

            const useSelectedProduct = (key: string | null) => {
                if (!key) return null
                return productComparisonAsync.value.find((p) => p.key === key) || null
            }

            const firstProduct = computed(() => useSelectedProduct(selectedItems.first))
            const secondProduct = computed(() => useSelectedProduct(selectedItems.second))
            const thirdProduct = computed(() => useSelectedProduct(selectedItems.third))

            watch(
                () => productComparisonAsync.value,
                (val) => {
                    if (val?.length) {
                        preselectDefaults()
                    }
                },
            )

            const productIds = computed<number[]>(() => {
                return [
                    firstProduct.value?.id,
                    secondProduct.value?.id,
                    thirdProduct.value?.id,
                ].filter((id): id is number => id !== undefined)
            })

            watch(productIds, (ids) => {
                filters.productKeys = ids
                scrollToTopOfPage()
            })

            const productPricesAsync = useProductComparisonPriceAsync(productIds, {
                evaluating: isBusyPrice,
            })

            // Preselect defaults for each dropdown
            const preselectDefaults = () => {
                const remainingOptions = [...productComparisonAsync.value]

                // Loop over each slot in selectedItems
                ;(Object.keys(selectedItems) as Array<keyof SelectedItems>).forEach((slot) => {
                    if (!selectedItems[slot] && remainingOptions.length) {
                        const nextProduct = remainingOptions.shift()
                        selectedItems[slot] = nextProduct ? nextProduct.key : null
                    }
                })
            }

            watch(
                () => filters.categoryKey,
                () => {
                    isBusyBase.value = true
                    isBusyProducts.value = true
                    isBusyPrice.value = true
                    selectedItems.first = null
                    selectedItems.second = null
                    selectedItems.third = null

                    const category = productCategoriesData.find(x => x.key === filters.categoryKey)
                    if (category) {
                        urlParams.category = category.title
                        scrollToTopOfPage()
                    }
                },
            )

            const mapCategoryToKey = (
                categoryName: string | null,
                productCategoriesData: ProductComparisonProductCategory[],
            ): string | null => {
                if (!categoryName || !productCategoriesData || productCategoriesData.length === 0) {
                    return null
                }

                const normalizedCategoryName = categoryName.replace(/-/g, ' ').toLowerCase()

                const matchedCategory = productCategoriesData.find(
                    (category) => category.title.toLowerCase() === normalizedCategoryName,
                )

                if (!matchedCategory) {
                    return null
                }

                return matchedCategory.key
            }

            const selectedProduct = (key: string) => {
                if (key === 'first') return firstProduct.value
                if (key === 'second') return secondProduct.value
                if (key === 'third') return thirdProduct.value
                return null
            }

            const selectedProducts = computed(() => {
                return (['first', 'second', 'third'] as Array<keyof SelectedItems>).map((key) => {
                    const selectedKey = selectedItems[key]
                    return (
                        productComparisonProductsAsync.value.find(
                            (product) => product.key === selectedKey,
                        ) || null
                    )
                })
            })

            const productCategoriesData = JSON.parse((props.productCategories || '[]')) as ProductComparisonProductCategory[]

            if (categoryParam) {
                const categoryKey = mapCategoryToKey(categoryParam, productCategoriesData)

                if (categoryKey) {
                    filters.categoryKey = categoryKey
                } else {
                    // Fall back to the first category in the list
                    if (productCategoriesData.length > 0) {
                        filters.categoryKey = productCategoriesData[0].key
                    }
                }
            } else {
                // Fallback if no query parameter provided
                const firstCategoryKey = props.firstProductCategory

                if (firstCategoryKey) {
                    filters.categoryKey = firstCategoryKey
                } else {
                    if (productCategoriesData.length > 0) {
                        filters.categoryKey = productCategoriesData[0].key
                    }
                }
            }

            const isBusy = computed(() => isBusyBase.value || isBusyProducts.value || isBusyPrice.value)

            return {
                isBusy,
                filters,
                productComparisonAsync,
                productComparisonProductsAsync,
                productPricesAsync,
                selectedItems,
                coverageGroups,
                selectedProducts,
                selectedProduct,
                isItemCovered,
                firstProduct,
                secondProduct,
                thirdProduct,
                isNavHidden,
                pageOffsetTop,
                targetIsVisible,
                target,
                mdAndGreater
            }
        },
    }),
})
